import { useI18n } from "../i18n/I18nProvider";
import { Card, Link, MethodBadge, Table, typographyClass } from "../ui-kit";
import type { HttpMethod } from "../ui-kit";

interface Endpoint {
  method: HttpMethod;
  path: string;
  upstream: string;
  description: string;
}

/**
 * Единый список ручек портала: он же документация, он же карта прокси.
 *
 * Колонка «апстрим» — путь в основном бэке. Витрина читается приватными ручками
 * под служебным токеном, всё остальное уходит в `/api/clients` под ключом
 * пользователя из заголовка `X-Api-Key`.
 */
const ENDPOINTS: Endpoint[] = [
  { method: "GET", path: "/api/models", upstream: "GET /api/v2/private/api_docs/nodes", description: "Каталог доступных нейросетей" },
  { method: "GET", path: "/api/models/{slug}", upstream: "то же + .../ml_data", description: "Описание модели и схема параметров" },
  { method: "POST", path: "/api/models/{slug}", upstream: "POST /tasks/", description: "Запуск задачи" },
  { method: "POST", path: "/api/models/{slug}/estimate", upstream: "POST /tasks/check_sum", description: "Оценка стоимости" },
  { method: "POST", path: "/api/models/{slug}/preview", upstream: "—", description: "Собрать тело задачи без запуска" },
  { method: "POST", path: "/api/uploads", upstream: "POST /storage/upload", description: "Загрузка файла для входа модели" },
  { method: "GET", path: "/api/tasks/{task_id}", upstream: "GET /tasks/status", description: "Статус и результат задачи" },
  { method: "GET", path: "/api/tasks/{task_id}/running", upstream: "GET /tasks/task-running", description: "Выполняется ли задача" },
  { method: "POST", path: "/api/tasks/{task_id}/cancel", upstream: "POST /tasks/cancel_task", description: "Отмена задачи с возвратом кредитов" },
  { method: "GET", path: "/api/tasks/history", upstream: "GET /user_info/history", description: "История запусков" },
  { method: "GET", path: "/api/projects", upstream: "GET /projects", description: "Проекты пользователя" },
  { method: "GET", path: "/api/projects/{id}/frames", upstream: "GET /projects/{id}/frames", description: "Фреймы проекта" },
  { method: "GET", path: "/api/frames/{frame_id}", upstream: "GET /frames/{id}", description: "Состав фрейма: ноды и переопределяемые поля" },
  { method: "POST", path: "/api/frames/{frame_id}/run", upstream: "POST /frames/{id}/run", description: "Запуск фрейма" },
  { method: "POST", path: "/api/frames/run-by-token", upstream: "POST /frames/run-by-token", description: "Запуск фрейма по токену" },
  { method: "GET", path: "/api/frames/{frame_id}/runs/{run_id}", upstream: "GET /frames/{id}/runs/{run}", description: "Статус запуска фрейма" },
  { method: "GET", path: "/api/frames/{frame_id}/runs/{run_id}/results", upstream: "GET /frames/{id}/runs/{run}/results", description: "Результаты запуска по нодам" },
  { method: "GET", path: "/api/workspaces", upstream: "GET /workspace/", description: "Список проектов (legacy workspace API)" },
  { method: "POST", path: "/api/workspaces/{id}/run", upstream: "POST /workspace/run", description: "Запуск пайплайна проекта" },
  { method: "GET", path: "/api/workspaces/pipelines/{id}", upstream: "GET /workspace/pipeline-status", description: "Статус пайплайна" },
  { method: "GET", path: "/api/health", upstream: "—", description: "Health-check портала" },
];

export function EndpointsPage() {
  const { t } = useI18n();

  return (
    <Card>
      <h1 className={typographyClass("header-24-semibold")}>{t("endpoints.title")}</h1>
      <p className={typographyClass("text-14-medium")} style={{ color: "var(--color-text-secondary-v2)", maxWidth: "68ch" }}>
        {t("endpoints.subtitle")}
      </p>

      <Table
        rows={ENDPOINTS}
        rowKey={(row) => `${row.method}-${row.path}`}
        columns={[
          { key: "method", header: "", render: (row) => <MethodBadge method={row.method} /> },
          {
            key: "path",
            header: t("endpoints.path"),
            render: (row) => <code className={typographyClass("mono-12")}>{row.path}</code>,
          },
          {
            key: "upstream",
            header: "/api/clients",
            render: (row) => <code className={typographyClass("mono-11")} style={{ color: "var(--color-text-secondary-v2)" }}>{row.upstream}</code>,
          },
          { key: "description", header: t("endpoints.description"), render: (row) => row.description },
        ]}
      />

      <Link href="/api/docs" target="_blank">
        OpenAPI / Swagger →
      </Link>
    </Card>
  );
}
